/**
 * Single source of truth for Claim Editor validation rules.
 * Used by store-level submit gating and per-form Formik `validate` adapters.
 */

import { ClaimSectionId } from './claim-editor-sections';

export const DISABLED_PAYER_MESSAGE = 'This payer has been disabled. Select a replacement.';
export const INVALID_DATE_TIME_MESSAGE = 'Enter a date as YYYY-MM-DD, optionally followed by HH:mm';
export const DISABLED_FACILITY_MESSAGE = 'This facility has been disabled. Select a replacement.';
export const DISABLED_PROVIDER_MESSAGE = 'This provider has been disabled. Select a replacement.';
export const DISABLED_BILLING_PROVIDER_MESSAGE =
    'This billing provider has been disabled. Select a replacement.';
export const DISABLED_PRIMARY_TPA_PAYER_MESSAGE =
    'Primary third party administrator payer has been disabled. Select a replacement.';
export const DISABLED_SECONDARY_TPA_PAYER_MESSAGE =
    'Secondary third party administrator payer has been disabled. Select a replacement.';
export const DISABLED_TERTIARY_TPA_PAYER_MESSAGE =
    'Tertiary third party administrator payer has been disabled. Select a replacement.';

/** X12 NM107 / Enter API name-suffix limit. Longer values are rejected by the backend. */
export const SUFFIX_MAX_LENGTH = 10;
export const SUFFIX_TOO_LONG_MESSAGE = `Suffix must be ${SUFFIX_MAX_LENGTH} characters or less`;

export interface ClaimFieldError {
    field: string;
    sectionId: ClaimSectionId;
    label: string;
    message: string;
}

export type ClaimErrorsByField = Record<string, ClaimFieldError>;

export interface IClaimEditorRulesData {
    patient: any;
    guarantor?: any;
    billingProviderId: string | null;
    services: any[];
    providers: any[];
    additionalInformation?: any | null;
    guarantorPayments?: Array<{
        collectionDate?: string;
        amount?: { amount?: string; currency?: string };
        memo?: string;
    }>;
    seller?: { externalId?: string; first?: string; last?: string; suffix?: string; organization?: string } | null;
    facility?: any | null;
    subscribers?: any[];
    inactivePayerIds?: string[];
    inactiveProviderIds?: string[];
    inactiveFacilityIds?: string[];
    primaryThirdPartyAdministratorPayerId?: string;
    secondaryThirdPartyAdministratorPayerId?: string;
    tertiaryThirdPartyAdministratorPayerId?: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isBlank(v: any): boolean {
    return v === undefined || v === null || (typeof v === 'string' && v.trim() === '');
}

/** Organization, both first and last, or all three. Empty first-only / last-only is not identity. */
function hasGuarantorIdentity(g: any): boolean {
    if (!g) return false;
    const hasOrganization = Boolean(String(g.organization || '').trim());
    const hasFirstAndLast = Boolean(String(g.first || '').trim() && String(g.last || '').trim());
    return hasOrganization || hasFirstAndLast;
}

function pushGuarantorNameErrors(out: ClaimFieldError[], g: any): void {
    if (hasGuarantorIdentity(g)) return;
    if (isBlank(g?.first)) pushError(out, 'guarantor.first', 'guarantor', 'First Name', 'First name is required');
    if (isBlank(g?.last)) pushError(out, 'guarantor.last', 'guarantor', 'Last Name', 'Last name is required');
}

export function isSuffixTooLong(value: unknown): boolean {
    if (value == null || value === '') return false;
    return String(value).length > SUFFIX_MAX_LENGTH;
}

function pushSuffixIfTooLong(
    out: ClaimFieldError[],
    suffix: unknown,
    field: string,
    sectionId: ClaimSectionId,
    label: string,
): void {
    if (isSuffixTooLong(suffix)) {
        pushError(out, field, sectionId, label, SUFFIX_TOO_LONG_MESSAGE);
    }
}

function pushError(
    out: ClaimFieldError[],
    field: string,
    sectionId: ClaimSectionId,
    label: string,
    message: string,
): void {
    out.push({ field, sectionId, label, message });
}

function addrLine1(entity: any): string | undefined {
    const addr = entity?.addresses?.[0];
    return Array.isArray(addr?.lines) ? addr.lines?.[0] : undefined;
}

function validateAddress(
    out: ClaimFieldError[],
    entity: any,
    sectionId: ClaimSectionId,
    prefix: string,
    fieldPrefix: string,
    options?: { requireCountry?: boolean },
): void {
    const addr = entity?.addresses?.[0] || {};
    const line1 = addrLine1(entity);
    if (!line1) pushError(out, `${fieldPrefix}.addresses[0].lines[0]`, sectionId, `${prefix} Address Line 1`, 'Address Line 1 is required');
    if (isBlank(addr?.city)) pushError(out, `${fieldPrefix}.addresses[0].city`, sectionId, `${prefix} City`, 'City is required');
    if (isBlank(addr?.state)) pushError(out, `${fieldPrefix}.addresses[0].state`, sectionId, `${prefix} State`, 'State is required');
    if (isBlank(addr?.postal)) pushError(out, `${fieldPrefix}.addresses[0].postal`, sectionId, `${prefix} Postal Code`, 'Postal Code is required');
    if (options?.requireCountry !== false && isBlank(addr?.country)) {
        pushError(out, `${fieldPrefix}.addresses[0].country`, sectionId, `${prefix} Country`, 'Country is required');
    }
}

function validateFacilityObject(
    out: ClaimFieldError[],
    facility: any,
    sectionId: ClaimSectionId,
    fieldPrefix: string,
    labelPrefix: string,
): void {
    if (!facility) return;
    const addr = facility?.addresses?.[0];
    const line1 = Array.isArray(addr?.lines) ? addr.lines?.[0] : undefined;
    if (isBlank(facility?.name)) pushError(out, `${fieldPrefix}.name`, sectionId, `${labelPrefix} Name`, 'Facility name is required');
    if (!line1) pushError(out, `${fieldPrefix}.addresses[0].lines[0]`, sectionId, `${labelPrefix} Address Line 1`, 'Address Line 1 is required');
    if (isBlank(addr?.city)) pushError(out, `${fieldPrefix}.addresses[0].city`, sectionId, `${labelPrefix} City`, 'City is required');
    if (isBlank(addr?.state)) pushError(out, `${fieldPrefix}.addresses[0].state`, sectionId, `${labelPrefix} State`, 'State is required');
    if (isBlank(addr?.country)) pushError(out, `${fieldPrefix}.addresses[0].country`, sectionId, `${labelPrefix} Country`, 'Country is required');
    if (isBlank(addr?.postal)) pushError(out, `${fieldPrefix}.addresses[0].postal`, sectionId, `${labelPrefix} Postal Code`, 'Postal Code is required');
    if (facility?.ein != null && String(facility.ein).length > 9) {
        pushError(out, `${fieldPrefix}.ein`, sectionId, `${labelPrefix} EIN`, 'EIN must be 9 characters or less');
    }
    if (facility?.npi != null && String(facility.npi).length > 10) {
        pushError(out, `${fieldPrefix}.npi`, sectionId, `${labelPrefix} NPI`, 'NPI must be 10 characters or less');
    }
}

function asIdSet(ids?: string[]): Set<string> {
    return new Set((ids || []).filter(Boolean));
}

function isInactiveId(id: string | null | undefined, inactiveIds: Set<string>): boolean {
    return Boolean(id && inactiveIds.has(id));
}

function pushInactiveEntityErrors(out: ClaimFieldError[], data: IClaimEditorRulesData): void {
    const inactivePayerIds = asIdSet(data.inactivePayerIds);
    const inactiveProviderIds = asIdSet(data.inactiveProviderIds);
    const inactiveFacilityIds = asIdSet(data.inactiveFacilityIds);

    if (isInactiveId(data.billingProviderId, inactiveProviderIds)) {
        pushError(out, 'billingProviderId', 'billingProvider', 'Billing Provider', DISABLED_BILLING_PROVIDER_MESSAGE);
    }
    if (isInactiveId(data.facility?.facilityId, inactiveFacilityIds)) {
        pushError(out, 'facility.facilityId', 'facility', 'Facility', DISABLED_FACILITY_MESSAGE);
    }
    if (isInactiveId(data.primaryThirdPartyAdministratorPayerId, inactivePayerIds)) {
        pushError(
            out,
            'primaryThirdPartyAdministratorPayerId',
            'settings',
            'Primary TPA Payer',
            DISABLED_PRIMARY_TPA_PAYER_MESSAGE,
        );
    }
    if (isInactiveId(data.secondaryThirdPartyAdministratorPayerId, inactivePayerIds)) {
        pushError(
            out,
            'secondaryThirdPartyAdministratorPayerId',
            'settings',
            'Secondary TPA Payer',
            DISABLED_SECONDARY_TPA_PAYER_MESSAGE,
        );
    }
    if (isInactiveId(data.tertiaryThirdPartyAdministratorPayerId, inactivePayerIds)) {
        pushError(
            out,
            'tertiaryThirdPartyAdministratorPayerId',
            'settings',
            'Tertiary TPA Payer',
            DISABLED_TERTIARY_TPA_PAYER_MESSAGE,
        );
    }

    (data.subscribers || []).forEach((sub: any, i: number) => {
        if (isInactiveId(sub?.payerId, inactivePayerIds)) {
            pushError(
                out,
                `subscribers[${i}].payerId`,
                'subscriber',
                `Subscriber ${i + 1} Payer`,
                `Subscriber ${i + 1}: ${DISABLED_PAYER_MESSAGE}`,
            );
        }
    });

    (data.providers || []).forEach((provider: any, i: number) => {
        if (isInactiveId(provider?.providerId, inactiveProviderIds)) {
            const providerType = provider?.type || 'Provider';
            pushError(
                out,
                `providers[${i}].providerId`,
                'additionalProviders',
                `${providerType} ${i + 1}`,
                `${providerType} ${i + 1}: ${DISABLED_PROVIDER_MESSAGE}`,
            );
        }
    });

    (data.services || []).forEach((service: any, i: number) => {
        const idx = i + 1;
        if (isInactiveId(service?.facility?.facilityId, inactiveFacilityIds)) {
            pushError(
                out,
                `services[${i}].facility.facilityId`,
                'services',
                `Service ${idx} Facility`,
                `Service line ${idx}: ${DISABLED_FACILITY_MESSAGE}`,
            );
        }
        const serviceProviders = toProviderList(service?.providers);
        serviceProviders.forEach((provider: any, pIdx: number) => {
            if (isInactiveId(provider?.providerId, inactiveProviderIds)) {
                const providerType = provider?.type || 'Provider';
                pushError(
                    out,
                    `services[${i}].providers[${pIdx}].providerId`,
                    'services',
                    `Service ${idx} ${providerType}`,
                    `Service line ${idx}: ${providerType} ${DISABLED_PROVIDER_MESSAGE}`,
                );
            }
        });
    });
}

/**
 * The wall-clock shapes `DateTimePickerField` accepts and `splitDateAndTime` can take apart:
 * `yyyy-MM-dd`, `yyyy-MM-dd HH:mm`, `yyyy-MM-dd HH:mm:ss` (see `date-time-picker.tsx`).
 *
 * A SPACE only — `splitDateAndTime` splits on whitespace, so an ISO `2026-05-28T14:30` leaves the
 * whole string as the date part and the value is silently dropped on the way to the API. That
 * silent drop is the reason this validation exists.
 */
const CLAIM_DATE_TIME_RE = /^(\d{4})-(\d{2})-(\d{2})(?: (\d{2}):(\d{2})(?::(\d{2}))?)?$/;

/** True when blank (optional) or a real calendar date/time in an accepted shape. */
function isValidClaimDateTime(value: any): boolean {
    if (isBlank(value)) return true;
    const match = CLAIM_DATE_TIME_RE.exec(String(value).trim());
    if (!match) return false;

    const [, y, mo, d, h, mi, sec] = match;
    const year = Number(y);
    const month = Number(mo);
    const day = Number(d);
    if (month < 1 || month > 12 || day < 1) return false;
    // Round-trip through Date so 2026-02-31 is rejected rather than rolling into March.
    const asDate = new Date(Date.UTC(year, month - 1, day));
    if (asDate.getUTCFullYear() !== year || asDate.getUTCMonth() !== month - 1 || asDate.getUTCDate() !== day) {
        return false;
    }
    if (h !== undefined && (Number(h) > 23 || Number(mi) > 59)) return false;
    if (sec !== undefined && Number(sec) > 59) return false;
    return true;
}

/**
 * Service-line providers, whatever shape they arrive in.
 *
 * `ProviderBlock` accepts an object as well as an array. Validation, the inactive-id collector,
 * and `updateServiceLineProvider` all used to bail with `[]` on the object shape — so a claim
 * loaded that way skipped identity, NPI AND disabled-provider checks and passed the submit gate.
 */
export function toProviderList(providers: any): any[] {
    if (Array.isArray(providers)) return providers;
    if (providers && typeof providers === 'object') return Object.values(providers);
    return [];
}

function validateProviderIdentity(
    out: ClaimFieldError[],
    provider: any,
    sectionId: ClaimSectionId,
    fieldPrefix: string,
    labelPrefix: string,
): void {
    if (!provider) return;
    const organization = (provider.organization || '').trim();
    const first = (provider.first || '').trim();
    const last = (provider.last || '').trim();
    const hasIdentity = !!organization || (!!first && !!last);
    if (!hasIdentity) {
        pushError(
            out,
            `${fieldPrefix}.organization`,
            sectionId,
            labelPrefix,
            'Either Organization or both First and Last name are required',
        );
    }
    if (isBlank(provider?.npi)) {
        pushError(out, `${fieldPrefix}.npi`, sectionId, `${labelPrefix} NPI`, 'NPI is required');
    } else if (String(provider.npi).trim().length !== 10) {
        pushError(out, `${fieldPrefix}.npi`, sectionId, `${labelPrefix} NPI`, 'NPI must be exactly 10 characters');
    }
}

/** Validate the full claim payload (store / submit gate). */
export function validateClaimPayload(data: IClaimEditorRulesData): ClaimFieldError[] {
    const out: ClaimFieldError[] = [];
    const {
        patient,
        guarantor,
        billingProviderId,
        services,
        providers,
        additionalInformation,
        guarantorPayments,
        seller,
        facility,
        subscribers,
    } = data;

    const p = patient as any;
    if (isBlank(p?.first)) pushError(out, 'patient.first', 'patient', 'First Name', 'First name is required');
    if (isBlank(p?.last)) pushError(out, 'patient.last', 'patient', 'Last Name', 'Last name is required');
    pushSuffixIfTooLong(out, p?.suffix, 'patient.suffix', 'patient', 'Suffix');
    if (isBlank(p?.dob)) pushError(out, 'patient.dob', 'patient', 'Date of Birth', 'Date of birth is required');
    if (isBlank(p?.sex)) pushError(out, 'patient.sex', 'patient', 'Sex', 'Sex is required');
    if (isBlank(p?.externalId)) {
        pushError(out, 'patient.externalId', 'patient', 'MRN', 'MRN is required');
    } else if (String(p.externalId).length > 38) {
        pushError(out, 'patient.externalId', 'patient', 'MRN', 'MRN must be 38 characters or less');
    }
    if (!isBlank(p?.emails?.[0]) && !EMAIL_RE.test(String(p.emails[0]))) {
        pushError(out, 'patient.emails[0]', 'patient', 'Email', 'Invalid email');
    }
    validateAddress(out, p, 'patient', 'Patient', 'patient');

    const g = guarantor as any;
    pushGuarantorNameErrors(out, g);
    pushSuffixIfTooLong(out, g?.suffix, 'guarantor.suffix', 'guarantor', 'Suffix');
    if (!isBlank(g?.email) && !EMAIL_RE.test(String(g.email))) {
        pushError(out, 'guarantor.email', 'guarantor', 'Email', 'Invalid email');
    }
    validateAddress(out, g, 'guarantor', 'Guarantor', 'guarantor');

    if (Array.isArray(subscribers) && subscribers.length > 0) {
        subscribers.forEach((sub: any, i: number) => {
            const idx = i + 1;
            const prefix = `subscribers[${i}]`;
            const labelPrefix = `Subscriber ${idx}`;
            if (isBlank(sub?.first)) pushError(out, `${prefix}.first`, 'subscriber', `${labelPrefix} First Name`, 'First name is required');
            if (isBlank(sub?.last)) pushError(out, `${prefix}.last`, 'subscriber', `${labelPrefix} Last Name`, 'Last name is required');
            pushSuffixIfTooLong(out, sub?.suffix, `${prefix}.suffix`, 'subscriber', `${labelPrefix} Suffix`);
            if (isBlank(sub?.dob)) {
                pushError(out, `${prefix}.dob`, 'subscriber', `${labelPrefix} Date of Birth`, 'Date of birth is required');
            }
            if (isBlank(sub?.payerId)) pushError(out, `${prefix}.payerId`, 'subscriber', `${labelPrefix} Payer`, 'Payer is required');
            if (isBlank(sub?.memberId)) pushError(out, `${prefix}.memberId`, 'subscriber', `${labelPrefix} Member ID`, 'Member ID is required');
            validateAddress(out, sub, 'subscriber', labelPrefix, prefix, { requireCountry: false });
        });
    }

    const servicesArray = Array.isArray(services) ? services : [];
    if (servicesArray.length === 0) {
        pushError(out, 'services', 'services', 'Services', 'At least one service line is required');
    } else {
        servicesArray.forEach((s: any, i: number) => {
            const idx = i + 1;
            const prefix = `services[${i}]`;
            const labelPrefix = `Service ${idx}`;
            if (isBlank(s?.procedureCode)) {
                pushError(out, `${prefix}.procedureCode`, 'services', `${labelPrefix} Procedure Code`, 'Procedure code is required');
            }
            if (isBlank(s?.fromDateOfService)) {
                pushError(out, `${prefix}.fromDateOfService`, 'services', `${labelPrefix} From DOS`, 'From date of service is required');
            }
            if (isBlank(s?.placeOfService)) {
                pushError(out, `${prefix}.placeOfService`, 'services', `${labelPrefix} Place of Service`, 'Place of service is required');
            }
            if (s?.units === undefined || s?.units === null || s?.units === '') {
                pushError(out, `${prefix}.units`, 'services', `${labelPrefix} Units`, 'Units are required');
            } else {
                const unitsNum = typeof s.units === 'number' ? s.units : parseFloat(String(s.units));
                if (!Number.isFinite(unitsNum) || unitsNum <= 0) {
                    pushError(out, `${prefix}.units`, 'services', `${labelPrefix} Units`, 'Units must be a number greater than 0');
                }
            }
            if (!Array.isArray(s?.diagnosisCodes) || s.diagnosisCodes.filter((c: any) => !isBlank(c)).length === 0) {
                pushError(out, `${prefix}.diagnosisCodes`, 'services', `${labelPrefix} Diagnosis Codes`, 'At least one diagnosis code is required');
            }

            validateFacilityObject(out, s?.facility, 'services', `${prefix}.facility`, `${labelPrefix} Facility`);

            // The error path uses a positional index either way, so for the object shape a jump
            // lands on the section rather than the exact field. Validated beats unvalidated.
            const serviceProviders = toProviderList(s?.providers);
            serviceProviders.forEach((provider: any, pIdx: number) => {
                const providerType = provider?.type || 'Provider';
                validateProviderIdentity(
                    out,
                    provider,
                    'services',
                    `${prefix}.providers[${pIdx}]`,
                    `${labelPrefix} ${providerType}`,
                );
                pushSuffixIfTooLong(
                    out,
                    provider?.suffix,
                    `${prefix}.providers[${pIdx}].suffix`,
                    'services',
                    `${labelPrefix} ${providerType} Suffix`,
                );
            });

            if (s?.drug) {
                const hasAny = Boolean(
                    s.drug.x12DrugQuantity || s.drug.x12NationalDrugCode || s.drug.x12DrugMeasurement,
                );
                if (hasAny) {
                    if (isBlank(s.drug.x12DrugQuantity)) {
                        pushError(out, `${prefix}.drug.x12DrugQuantity`, 'services', `${labelPrefix} Drug Quantity`, 'Drug quantity is required');
                    }
                    if (isBlank(s.drug.x12NationalDrugCode)) {
                        pushError(out, `${prefix}.drug.x12NationalDrugCode`, 'services', `${labelPrefix} Drug NDC`, 'Drug NDC is required');
                    }
                    if (isBlank(s.drug.x12DrugMeasurement)) {
                        pushError(out, `${prefix}.drug.x12DrugMeasurement`, 'services', `${labelPrefix} Drug Measurement`, 'Drug measurement is required');
                    }
                }
            }
        });
    }

    if (isBlank(billingProviderId)) {
        pushError(out, 'billingProviderId', 'billingProvider', 'Billing Provider', 'Billing provider is required');
    }

    validateFacilityObject(out, facility, 'facility', 'facility', 'Facility');

    (Array.isArray(providers) ? providers : []).forEach((provider: any, i: number) => {
        const providerType = provider?.type || 'Provider';
        validateProviderIdentity(out, provider, 'additionalProviders', `providers[${i}]`, `${providerType}`);
        pushSuffixIfTooLong(
            out,
            provider?.suffix,
            `providers[${i}].suffix`,
            'additionalProviders',
            `${providerType} Suffix`,
        );
    });

    /**
     * Admission / discharge are OPTIONAL, deliberately, and this only checks the FORMAT.
     *
     * "Required" was the answer given when this was raised, but a real COMPLETED professional
     * claim on a live account carries `admissionDate: null` / `dischargeDate: null`, and this
     * editor builds `ProviderCreateClaimTypePayload` — the professional shape. Requiring them
     * would block every professional claim, which is the common case. Ordering is not checked
     * either: a discharge before an admission is left to the payer's own date edits.
     */
    const info = additionalInformation as any;
    if (!isValidClaimDateTime(info?.admissionDateTime)) {
        pushError(out, 'additionalInformation.admissionDateTime', 'additionalInformation', 'Admission Date/Time', INVALID_DATE_TIME_MESSAGE);
    }
    if (!isValidClaimDateTime(info?.dischargeDateTime)) {
        pushError(out, 'additionalInformation.dischargeDateTime', 'additionalInformation', 'Discharge Date/Time', INVALID_DATE_TIME_MESSAGE);
    }

    const controlNumber = (additionalInformation as any)?.controlNumber;
    if (isBlank(controlNumber)) {
        pushError(out, 'additionalInformation.controlNumber', 'additionalInformation', 'Control Number', 'Control number is required');
    } else if (String(controlNumber).trim().length > 20) {
        pushError(
            out,
            'additionalInformation.controlNumber',
            'additionalInformation',
            'Control Number',
            'Control number must be at most 20 characters',
        );
    }

    const payments = Array.isArray(guarantorPayments) ? guarantorPayments : [];
    payments.forEach((pay, i) => {
        const hasAny = Boolean(pay?.collectionDate || pay?.amount?.amount);
        if (!hasAny) return;
        const prefix = `guarantorPayments[${i}]`;
        const labelPrefix = `Guarantor Pre-payment ${i + 1}`;
        if (isBlank(pay?.collectionDate)) {
            pushError(out, `${prefix}.collectionDate`, 'settings', `${labelPrefix} Date`, 'Valid date is required');
        }
        const amtNum = parseFloat(pay?.amount?.amount || '');
        if (isBlank(pay?.amount?.amount) || isNaN(amtNum) || amtNum <= 0) {
            pushError(out, `${prefix}.amount`, 'settings', `${labelPrefix} Amount`, 'Amount must be greater than 0');
        }
    });

    if (seller) {
        const hasAnySeller = Boolean(seller.externalId || seller.organization || seller.first || seller.last);
        if (hasAnySeller && isBlank(seller.externalId)) {
            pushError(out, 'seller.externalId', 'settings', 'Seller External System Id', 'External System Id is required');
        }
        pushSuffixIfTooLong(out, seller.suffix, 'seller.suffix', 'settings', 'Seller Suffix');
    }

    pushInactiveEntityErrors(out, data);

    return out;
}

export function errorsByField(errors: ClaimFieldError[]): ClaimErrorsByField {
    const map: ClaimErrorsByField = {};
    errors.forEach(e => {
        if (!map[e.field]) map[e.field] = e;
    });
    return map;
}

export function groupErrorsBySection(errors: ClaimFieldError[]): Partial<Record<ClaimSectionId, ClaimFieldError[]>> {
    const groups: Partial<Record<ClaimSectionId, ClaimFieldError[]>> = {};
    errors.forEach(e => {
        if (!groups[e.sectionId]) groups[e.sectionId] = [];
        groups[e.sectionId]!.push(e);
    });
    return groups;
}

export function sectionHasContent(sectionId: ClaimSectionId, data: IClaimEditorRulesData): boolean {
    switch (sectionId) {
        case 'patient':
            return Boolean(data.patient && (data.patient.first || data.patient.last || data.patient.externalId));
        case 'guarantor':
            // Identity is organization, first+last, or both — not first/last alone.
            return hasGuarantorIdentity(data.guarantor);
        case 'subscriber':
            return Array.isArray(data.subscribers) && data.subscribers.length > 0;
        case 'services':
            return Array.isArray(data.services) && data.services.length > 0;
        case 'billingProvider':
            return Boolean(data.billingProviderId);
        case 'facility':
            return Boolean(data.facility);
        case 'additionalProviders':
            return Array.isArray(data.providers) && data.providers.length > 0;
        case 'additionalInformation':
            return Boolean((data.additionalInformation as any)?.controlNumber);
        case 'settings': {
            const hasPayments = Array.isArray(data.guarantorPayments) && data.guarantorPayments.length > 0;
            const seller = data.seller;
            const hasSeller = Boolean(seller && (seller.externalId || seller.organization || seller.first || seller.last));
            const hasTpa = Boolean(
                data.primaryThirdPartyAdministratorPayerId ||
                    data.secondaryThirdPartyAdministratorPayerId ||
                    data.tertiaryThirdPartyAdministratorPayerId,
            );
            return hasPayments || hasSeller || hasTpa;
        }
        default:
            return false;
    }
}

/** Formik-compatible flat errors for patient form values. */
export function validatePatientFormValues(values: any): Record<string, string> {
    const errors: Record<string, string> = {};
    if (isBlank(values?.first)) errors.first = 'First name is required';
    if (isBlank(values?.last)) errors.last = 'Last name is required';
    if (isSuffixTooLong(values?.suffix)) errors.suffix = SUFFIX_TOO_LONG_MESSAGE;
    if (isBlank(values?.dob)) errors.dob = 'Date of birth is required';
    if (isBlank(values?.sex)) errors.sex = 'Sex is required';
    if (isBlank(values?.externalId)) errors.externalId = 'MRN is required';
    else if (String(values.externalId).length > 38) errors.externalId = 'MRN must be 38 characters or less';
    if (!isBlank(values?.email) && !EMAIL_RE.test(String(values.email))) errors.email = 'Invalid email';
    if (isBlank(values?.address1)) errors.address1 = 'Address Line 1 is required';
    if (isBlank(values?.city)) errors.city = 'City is required';
    if (isBlank(values?.state)) errors.state = 'State is required';
    if (isBlank(values?.country)) errors.country = 'Country is required';
    if (isBlank(values?.postal)) errors.postal = 'Postal Code is required';
    return errors;
}

/** Formik-compatible flat errors for guarantor form values. */
export function validateGuarantorFormValues(values: any): Record<string, string> {
    const errors: Record<string, string> = {};
    if (!hasGuarantorIdentity(values)) {
        if (isBlank(values?.first)) errors.first = 'First name is required';
        if (isBlank(values?.last)) errors.last = 'Last name is required';
    }
    if (isSuffixTooLong(values?.suffix)) errors.suffix = SUFFIX_TOO_LONG_MESSAGE;
    if (!isBlank(values?.email) && !EMAIL_RE.test(String(values.email))) errors.email = 'Invalid email';
    if (isBlank(values?.address1)) errors.address1 = 'Address Line 1 is required';
    if (isBlank(values?.city)) errors.city = 'City is required';
    if (isBlank(values?.state)) errors.state = 'State is required';
    if (isBlank(values?.country)) errors.country = 'Country is required';
    if (isBlank(values?.postal)) errors.postal = 'Postal Code is required';
    return errors;
}

/** Formik-compatible flat errors for subscriber form values. */
export function validateSubscriberFormValues(
    values: any,
    options?: { isPayerIdActive?: (id?: string | null) => boolean },
): Record<string, string> {
    const errors: Record<string, string> = {};
    // The store gate requires `payerId`, so this must too. Accepting a non-blank `payerLookup`
    // on its own let a typed-but-never-selected payer pass form validation, which suppressed the
    // inline errors under the inputs while submission stayed blocked with no visible reason.
    if (isBlank(values?.payerId)) {
        errors.payerId = 'Payer is required';
        errors.payerLookup = isBlank(values?.payerLookup)
            ? 'Payer is required'
            : 'Select a payer from the list';
    } else if (options?.isPayerIdActive && !options.isPayerIdActive(values.payerId)) {
        errors.payerLookup = DISABLED_PAYER_MESSAGE;
        errors.payerId = DISABLED_PAYER_MESSAGE;
    }
    if (isBlank(values?.memberId)) errors.memberId = 'Member ID is required';
    if (isBlank(values?.first)) errors.first = 'First name is required';
    if (isBlank(values?.last)) errors.last = 'Last name is required';
    if (isSuffixTooLong(values?.suffix)) errors.suffix = SUFFIX_TOO_LONG_MESSAGE;
    if (isBlank(values?.dob)) errors.dob = 'Date of birth is required';
    if (isBlank(values?.address1)) errors.address1 = 'Address Line 1 is required';
    if (isBlank(values?.city)) errors.city = 'City is required';
    if (isBlank(values?.state)) errors.state = 'State is required';
    if (isBlank(values?.postal)) errors.postal = 'Postal Code is required';
    return errors;
}

/** Formik-compatible flat errors for billing provider lookup. */
export function validateBillingProviderFormValues(
    values: any,
    options?: { isProviderIdActive?: (id?: string | null) => boolean },
): Record<string, string> {
    const errors: Record<string, string> = {};
    if (isBlank(values?.providerLookup)) errors.providerLookup = 'Billing provider is required';
    else if (options?.isProviderIdActive && !options.isProviderIdActive(values.providerLookup)) {
        errors.providerLookup = DISABLED_BILLING_PROVIDER_MESSAGE;
    }
    return errors;
}

/** Formik-compatible flat errors for additional information form values. */
export function validateAdditionalInformationFormValues(values: any): Record<string, string> {
    const errors: Record<string, string> = {};
    if (isBlank(values?.controlNumber)) errors.controlNumber = 'Control number is required';
    else if (String(values.controlNumber).trim().length > 20) {
        errors.controlNumber = 'Control number must be at most 20 characters';
    }
    // Both stay OPTIONAL — see the note on the payload-level check in validateClaimPayload.
    if (!isValidClaimDateTime(values?.admissionDateTime)) {
        errors.admissionDateTime = INVALID_DATE_TIME_MESSAGE;
    }
    if (!isValidClaimDateTime(values?.dischargeDateTime)) {
        errors.dischargeDateTime = INVALID_DATE_TIME_MESSAGE;
    }
    return errors;
}

/** Formik-compatible flat errors for facility form values. */
export function validateFacilityFormValues(
    values: any,
    options?: { isFacilityIdActive?: (id?: string | null) => boolean },
): Record<string, string> {
    const errors: Record<string, string> = {};
    if (values?.facilityId && options?.isFacilityIdActive && !options.isFacilityIdActive(values.facilityId)) {
        errors.facilityLookup = DISABLED_FACILITY_MESSAGE;
        errors.facilityId = DISABLED_FACILITY_MESSAGE;
    }
    if (isBlank(values?.name)) errors.name = 'Facility name is required';
    if (isBlank(values?.addressLine1)) errors.addressLine1 = 'Address Line 1 is required';
    if (isBlank(values?.city)) errors.city = 'City is required';
    if (isBlank(values?.state)) errors.state = 'State is required';
    if (isBlank(values?.postalCode)) errors.postalCode = 'Postal Code is required';
    if (isBlank(values?.country)) errors.country = 'Country is required';
    if (!isBlank(values?.ein) && String(values.ein).length > 9) errors.ein = 'EIN must be 9 characters or less';
    if (!isBlank(values?.npi) && String(values.npi).length > 10) errors.npi = 'NPI must be 10 characters or less';
    return errors;
}

/** Formik-compatible flat errors for additional / service-line provider form values. */
export function validateProviderFormValues(
    values: any,
    options?: { isProviderIdActive?: (id?: string | null) => boolean },
): Record<string, string> {
    const errors: Record<string, string> = {};
    if (values?.providerId && options?.isProviderIdActive && !options.isProviderIdActive(values.providerId)) {
        errors.providerLookup = DISABLED_PROVIDER_MESSAGE;
        errors.providerId = DISABLED_PROVIDER_MESSAGE;
    }
    const organization = (values?.organization || '').trim();
    const first = (values?.first || '').trim();
    const last = (values?.last || '').trim();
    const hasIdentity = !!organization || (!!first && !!last);
    if (!hasIdentity) {
        errors.organization = 'Either Organization or both First and Last name are required';
    }
    if (isSuffixTooLong(values?.suffix)) errors.suffix = SUFFIX_TOO_LONG_MESSAGE;
    if (isBlank(values?.npi)) errors.npi = 'NPI is required';
    else if (String(values.npi).trim().length !== 10) errors.npi = 'NPI must be exactly 10 characters';
    return errors;
}

/** Formik-compatible flat errors for service item form values. */
export function validateServiceItemFormValues(values: any): Record<string, unknown> {
    const errors: Record<string, unknown> = {};
    if (isBlank(values?.fromDos)) errors.fromDos = 'From date of service is required';
    if (isBlank(values?.procedureCode)) errors.procedureCode = 'Procedure code is required';
    if (isBlank(values?.placeOfService)) errors.placeOfService = 'Place of service is required';

    if (values?.units === '' || values?.units === undefined || values?.units === null) {
        errors.units = 'Units are required';
    } else {
        const unitsNum = typeof values.units === 'number' ? values.units : parseFloat(String(values.units));
        if (!Number.isFinite(unitsNum)) errors.units = 'Units must be a number';
        else if (unitsNum <= 0) errors.units = 'Units must be greater than 0';
    }

    const codes = Array.isArray(values?.codes) ? values.codes.filter((c: any) => !isBlank(c)) : [];
    if (codes.length === 0) {
        // The input is bound to `codes.0`, and Formik resolves that path by walking
        // errors.codes[0]. A string here resolves to its first CHARACTER ("A"), and a literal
        // 'codes.0' key is never consulted — so the field showed nothing useful either way.
        // An array puts the message where both the field and the summary can read it.
        errors.codes = ['At least one diagnosis code is required'];
    }

    const hasAnyDrug = Boolean(values?.drugQuantity || values?.drugNdc || values?.drugMeasurement);
    if (hasAnyDrug) {
        if (isBlank(values?.drugQuantity)) errors.drugQuantity = 'Drug quantity is required';
        if (isBlank(values?.drugNdc)) errors.drugNdc = 'Drug NDC is required';
        if (isBlank(values?.drugMeasurement)) errors.drugMeasurement = 'Drug measurement is required';
    }

    return errors;
}
